import { FilterOption, XgacFilter } from './types';

export const NEW_FILTER_KEY_PREFIX = '__new__';

export const isNewFilterKey = (key: string): boolean => key.startsWith(NEW_FILTER_KEY_PREFIX);

const RESULT_ONLY_FILTER_KEYS = ['distance', 'contacted', 'available', 'assetId', 'customerId'];

export const COMPOUND_FILTER_FAMILIES = ['groups', 'insideZones', 'embeddedTimestamp', 'id'] as const;

export type CompoundFilterFamily = typeof COMPOUND_FILTER_FAMILIES[number];

export const FILTER_FAMILY_LABELS: Record<string, string> = {
  groups: 'Group',
  insideZones: 'Inside zone',
  embeddedTimestamp: 'Last observation',
  id: 'Asset id',
};

export const COMPOUND_COMPARATOR_LABELS: Record<string, string> = {
  equals: 'Equals',
  'not-equals': 'Not equals',
  contains: 'Contains',
  'not-contains': 'Not contains',
  younger: 'Younger than (seconds)',
  older: 'Older than (seconds)',
};

const EMBEDDED_TIMESTAMP_MS_PER_SECOND = 1000;

export const isEmbeddedTimestampFilterKey = (key: string): boolean => key.startsWith('embeddedTimestamp');

export const embeddedTimestampToDisplaySeconds = (storedValue: string): string => {

  if (!storedValue) return '';

  const milliseconds = Number(storedValue);
  if (Number.isNaN(milliseconds)) return storedValue;

  return String(milliseconds / EMBEDDED_TIMESTAMP_MS_PER_SECOND);

};

export const embeddedTimestampToStoredMilliseconds = (secondsValue: string): string => {

  if (!secondsValue) return '';

  const seconds = Number(secondsValue);
  if (Number.isNaN(seconds)) return secondsValue;

  return String(Math.round(seconds * EMBEDDED_TIMESTAMP_MS_PER_SECOND));

};

const COMPOUND_COMPARATOR_ORDER = ['equals', 'not-equals', 'contains', 'not-contains', 'younger', 'older'];

/**
 * All filter keys accepted by ATS getAssets.
 */
export const ATS_FILTER_OPTIONS: FilterOption[] = [
  { value: 'embeddedTimestamp:younger', label: 'Last observation younger than (seconds)' },
  { value: 'embeddedTimestamp:older', label: 'Last observation older than (seconds)' },
  { value: 'groups:equals', label: 'Group equals' },
  { value: 'groups:not-equals', label: 'Group not equals' },
  { value: 'groups:contains', label: 'Group contains' },
  { value: 'groups:not-contains', label: 'Group not contains' },
  { value: 'insideZones:equals', label: 'Inside zone equals' },
  { value: 'insideZones:not-equals', label: 'Inside zone not equals' },
  { value: 'insideZones:contains', label: 'Inside zone contains' },
  { value: 'insideZones:not-contains', label: 'Inside zone not contains' },
  { value: 'id:equals', label: 'Asset id equals' },
  { value: 'id:not-equals', label: 'Asset id not equals' },
  { value: 'available', label: 'Available' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'distance', label: 'Distance (meters)' },
  { value: 'assetId', label: 'Asset id' },
  { value: 'customerId', label: 'Customer id' },
];

export const parseCompoundFilterKey = (key: string): { family: CompoundFilterFamily; comparator: string } | null => {

  const separatorIndex = key.indexOf(':');
  if (separatorIndex === -1) return null;

  const family = key.substring(0, separatorIndex);
  if (!COMPOUND_FILTER_FAMILIES.includes(family as CompoundFilterFamily)) return null;

  return {
    family: family as CompoundFilterFamily,
    comparator: key.substring(separatorIndex + 1),
  };

};

export const buildCompoundFilterKey = (family: string, comparator: string): string => `${family}:${comparator}`;

export const isCompoundFilterFamily = (value: string): boolean => (
  COMPOUND_FILTER_FAMILIES.includes(value as CompoundFilterFamily)
);

export const isCompoundFilterKey = (key: string): boolean => parseCompoundFilterKey(key) !== null;

export const getFilterFamily = (key: string): string => parseCompoundFilterKey(key)?.family ?? key;

const collectFullFilterOptions = (presetOptions: FilterOption[]): FilterOption[] => {

  const merged = new Map<string, FilterOption>();
  for (const option of [...ATS_FILTER_OPTIONS, ...presetOptions]) {

    merged.set(option.value, option);

  }
  return Array.from(merged.values());

};

export const getComparatorsForFamily = (family: string, presetOptions: FilterOption[]): FilterOption[] => {

  const comparators = new Map<string, FilterOption>();

  for (const option of collectFullFilterOptions(presetOptions)) {

    const parsed = parseCompoundFilterKey(option.value);
    if (!parsed || parsed.family !== family) continue;

    comparators.set(parsed.comparator, {
      value: parsed.comparator,
      label: COMPOUND_COMPARATOR_LABELS[parsed.comparator] || parsed.comparator,
    });

  }

  return Array.from(comparators.values()).sort((a, b) => {

    const indexA = COMPOUND_COMPARATOR_ORDER.indexOf(a.value);
    const indexB = COMPOUND_COMPARATOR_ORDER.indexOf(b.value);
    return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);

  });

};

export const getDefaultComparator = (family: string, presetOptions: FilterOption[]): string => (
  getComparatorsForFamily(family, presetOptions)[0]?.value || 'equals'
);

export const resolveFilterKey = (keyOrFamily: string, presetOptions: FilterOption[]): string => {

  if (isNewFilterKey(keyOrFamily)) return keyOrFamily;
  if (parseCompoundFilterKey(keyOrFamily)) return keyOrFamily;
  if (isCompoundFilterFamily(keyOrFamily)) {

    return buildCompoundFilterKey(keyOrFamily, getDefaultComparator(keyOrFamily, presetOptions));

  }
  return keyOrFamily;

};

const toSelectorOption = (option: FilterOption): FilterOption => {

  const parsed = parseCompoundFilterKey(option.value);
  if (!parsed) return option;

  return {
    value: parsed.family,
    label: FILTER_FAMILY_LABELS[parsed.family] || parsed.family,
  };

};

export const getSelectorFilterOptions = (presetOptions: FilterOption[]): FilterOption[] => {

  const merged = new Map<string, FilterOption>();

  for (const option of collectFullFilterOptions(presetOptions)) {

    const selectorOption = toSelectorOption(option);
    merged.set(selectorOption.value, selectorOption);

  }

  return Array.from(merged.values()).sort((a, b) => a.label.localeCompare(b.label));

};

export const isResultOnlyFilterKey = (key: string): boolean => RESULT_ONLY_FILTER_KEYS.includes(key);

export const isValidFilterKey = (key: string): boolean => {

  if (isResultOnlyFilterKey(key)) return true;
  return (
    key.startsWith('embeddedTimestamp')
    || key.startsWith('insideZones')
    || key.startsWith('groups')
    || key.startsWith('id')
  );

};

export const mergeFilterOptions = (
  presetOptions: FilterOption[],
  activeFilters: Record<string, string>,
): FilterOption[] => {

  const merged = new Map<string, FilterOption>();

  for (const option of collectFullFilterOptions(presetOptions)) {

    const selectorOption = toSelectorOption(option);
    merged.set(selectorOption.value, selectorOption);

  }

  for (const key of Object.keys(activeFilters)) {

    if (isNewFilterKey(key)) continue;

    const selectorOption = isCompoundFilterKey(key)
      ? {
        value: getFilterFamily(key),
        label: FILTER_FAMILY_LABELS[getFilterFamily(key)] || getFilterFamily(key),
      }
      : { value: key, label: key };

    if (!merged.has(selectorOption.value)) {

      merged.set(selectorOption.value, selectorOption);

    }

  }

  return Array.from(merged.values()).sort((a, b) => a.label.localeCompare(b.label));

};

export const flattenXgacFilter = (filter: XgacFilter): Record<string, string> => ({
  ...(filter.request || {}),
  ...(filter.result || {}),
});

export const splitXgacFilter = (
  flatFilters: Record<string, string>,
  max?: number,
): XgacFilter => {

  const request: Record<string, string> = {};
  const result: Record<string, string> = {};

  for (const [key, value] of Object.entries(flatFilters)) {

    if (isNewFilterKey(key)) continue;
    if (isResultOnlyFilterKey(key)) {

      result[key] = value;

    } else {

      request[key] = value;

    }

  }

  return {
    request,
    result,
    ...(typeof max === 'number' ? { max } : {}),
  };

};
